import React, { useEffect, useRef, useState } from 'react';
import { AssignmentWizardPageProps } from '../AssignmentWizard';

export interface SectionPageProps {
  courseSectionIds: string[];
  sectioned: boolean;
}

function SectionPage({
  assignmentSettings,
  courseSections,
  updateSettings,
}: AssignmentWizardPageProps<SectionPageProps>): JSX.Element {
  const [courseSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [sectioned, setSectioned] = useState<boolean>(false);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && assignmentSettings && courseSections) {
      // For existing assignments, use saved values
      if (assignmentSettings.sectioned !== undefined) {
        setSectioned(assignmentSettings.sectioned);
        if (assignmentSettings.sectioned && assignmentSettings.courseSectionIds) {
          setSelectedSectionIds(assignmentSettings.courseSectionIds);
        } else {
          // Not sectioned - select all
          setSelectedSectionIds(courseSections.map((section) => section.courseSectionId));
        }
      } else {
        // New assignment - default to all sections, not sectioned
        setSelectedSectionIds(courseSections.map((section) => section.courseSectionId));
        setSectioned(false);
      }
      hasInitialized.current = true;
    }
  }, [assignmentSettings, courseSections]);

  const handleChange = (sectionId: string) => {
    setSelectedSectionIds((prevSelected) =>
      prevSelected.includes(sectionId) ? prevSelected.filter((id) => id !== sectionId) : [...prevSelected, sectionId],
    );
  };

  const handleSectionedChange = (newSectioned: boolean) => {
    setSectioned(newSectioned);
    if (!newSectioned && courseSections) {
      // When switching to "all students", select all sections
      setSelectedSectionIds(courseSections.map((section) => section.courseSectionId));
    }
  };

  useEffect(() => {
    updateSettings({ courseSectionIds, sectioned });
  }, [updateSettings, courseSectionIds, sectioned]);

  return (
    <div>
      <label>Assignment Visibility:</label>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <div className="rad-radio-btn">
          <input
            id="assignment-visibility-all"
            type="radio"
            name="assignment-visibility"
            value="all"
            checked={!sectioned}
            onChange={() => handleSectionedChange(false)}
          />
          <label htmlFor="assignment-visibility-all">Available to All Students</label>
        </div>
        <div className="rad-radio-btn">
          <input
            id="assignment-visibility-specific"
            type="radio"
            name="assignment-visibility"
            value="specific"
            checked={sectioned}
            onChange={() => handleSectionedChange(true)}
          />
          <label htmlFor="assignment-visibility-specific">Limit to Specific Sections</label>
        </div>
      </div>
      <div>
        {courseSections && courseSections.length > 0 ? (
          courseSections.map((section) => (
            <label key={section.courseSectionId} style={{ display: 'block' }}>
              <input
                type="checkbox"
                value={section.courseSectionId}
                checked={courseSectionIds.includes(section.courseSectionId)}
                onChange={() => handleChange(section.courseSectionId)}
                disabled={!sectioned}
              />
              {section.courseSectionName}
            </label>
          ))
        ) : (
          <p>No course sections available.</p>
        )}
      </div>
    </div>
  );
}

export default SectionPage;
